import React, { useEffect, useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  MenuItem,
  TextField,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import ExpandMore from '@material-ui/icons/ExpandMore';
import SweetAlert from 'react-bootstrap-sweetalert';
import sweetStyles from 'assets/jss/material-dashboard-pro-react/views/sweetAlertStyle.js';
import ModalBase from 'components/Tvendo/Common/ModalBase';
import GridContainer from 'components/Grid/GridContainer';
import GridItem from 'components/Grid/GridItem';
import RegularButton from 'components/CustomButtons/Button';
import LoadingSpinner from 'components/Tvendo/Common/LoadingSpinner';
import Table from 'components/Table/Table';
import { wsGetAll, wsCreate } from 'services/WService';
import moment from 'moment';

const useStyles = makeStyles(sweetStyles);

/**
 * Confirmación antes de aplicar las NC de varios socios.
 *
 * rows son los documentos validados del listado (una fila por documento). Se
 * consulta cada socio con los filtros del detalle y se mandan solo los
 * documentos que estaban validados.
 */
function ProcessNCModal({ model, setOpen, rows = [], reload }) {
  const classes = useStyles();
  const [zones, setZones] = useState([]);
  const [filter, setFilter] = useState({
    start_date: '',
    end_date: '',
    base_zone_id: '',
    type: 'no_aplicados',
  });
  const [porSocio, setPorSocio] = useState([]);
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [alerta, setAlerta] = useState(null);

  const documents = useMemo(() => porSocio.flatMap((socio) => socio.documents), [porSocio]);

  async function loadZones() {
    if (zones.length > 0) return;
    let res = await wsGetAll('base/zone');
    if (res.success) setZones(res.data);
  }

  async function search(current) {
    setLoading(true);
    let resultado = [];

    // Los socios de los documentos validados, sin repetir
    let socios = [];
    for (let row of rows) {
      if (!socios.includes(row.CardCode)) socios.push(row.CardCode);
    }

    // Una consulta por socio: es la misma que hace el detalle al entrar.
    for (let cardCode of socios) {
      let validadosSocio = rows.filter((row) => row.CardCode === cardCode);
      let docEntries = validadosSocio.map((row) => String(row.DocEntry));

      let queryString =
        `?CardCode=${cardCode}` +
        `&start_date=${current.start_date}&end_date=${current.end_date}&type=${current.type}`;
      if (!!current.base_zone_id) queryString += `&base_zone_id=${current.base_zone_id}`;

      let res = await wsGetAll(`pymt/received-payment-cn${queryString}`);
      // Solo los documentos que se validaron en el listado
      let docs = res.success
        ? res.data.filter(
            (doc) =>
              doc.ObjType === '13' && !!doc.CreditNotesAgg && docEntries.includes(String(doc.DocEntry)),
          )
        : [];

      resultado.push({
        CardCode: cardCode,
        CardName: validadosSocio[0].CardName,
        detalle: docs,
        documents: docs.map((doc) => ({
          DocEntry: doc.DocEntry,
          ObjType: doc.ObjType,
          CreditNotes: doc.CreditNotesAgg.split(','),
        })),
      });
    }

    setPorSocio(resultado);
    setLoading(false);
  }

  /** El guardado no pasa por ModalBase: primero hay que confirmar. */
  function confirmar() {
    setAlerta(
      <SweetAlert
        warning
        style={{ display: 'block', marginTop: '-100px' }}
        title="¿Está seguro de procesar estos documentos?"
        onConfirm={() => {
          setAlerta(null);
          procesar();
        }}
        onCancel={() => setAlerta(null)}
        confirmBtnCssClass={classes.button + ' ' + classes.success}
        cancelBtnCssClass={classes.button + ' ' + classes.danger}
        confirmBtnText="Aceptar"
        cancelBtnText="Cancelar"
        showCancel
      >
        Se van a aplicar las notas de crédito de {documents.length} documento(s). Esta acción no se
        puede revertir.
      </SweetAlert>,
    );
  }

  async function procesar() {
    setProcessing(true);
    let res = await wsCreate('pymt/received-payment-cn', { documents });
    setProcessing(false);

    if (!res.success) {
      window.alert(res.message);
      return;
    }

    window.alert(res.message);
    setOpen({ open: false });
    if (reload) reload();
  }

  useEffect(() => {
    if (!model.open || rows.length === 0) return;

    // El rango arranca cubriendo a todos los documentos validados.
    let fechas = rows
      .map((row) => row.TaxDate)
      .filter(Boolean)
      .map((fecha) => moment(fecha));

    let inicial = {
      start_date: moment.min(fechas).format('YYYY-MM-DD'),
      end_date: moment.max(fechas).format('YYYY-MM-DD'),
      base_zone_id: '',
      type: 'no_aplicados',
    };

    setFilter(inicial);
    setPorSocio([]);
    loadZones();
    search(inicial);
  }, [model.open]);

  return (
    <>
      {alerta}
      <ModalBase
        title="Procesar notas de crédito"
        model={model}
        setOpen={setOpen}
        hideSave
        footerComponent={
          <RegularButton
            type="button"
            color="success"
            size="sm"
            disabled={loading || processing || documents.length === 0}
            onClick={confirmar}
          >
            Procesar
          </RegularButton>
        }
        fullWidth
        maxWidth="md"
      >
        <GridContainer>
          <GridItem xs={12} sm={6}>
            <TextField
              label="Desde"
              type="date"
              fullWidth
              value={filter.start_date}
              InputLabelProps={{ shrink: true }}
              inputProps={{ max: filter.end_date }}
              onChange={(e) => setFilter({ ...filter, start_date: e.target.value })}
            />
          </GridItem>
          <GridItem xs={12} sm={6}>
            <TextField
              label="Hasta"
              type="date"
              fullWidth
              value={filter.end_date}
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: filter.start_date }}
              onChange={(e) => setFilter({ ...filter, end_date: e.target.value })}
            />
          </GridItem>
          <GridItem xs={12} sm={6}>
            <TextField
              label="Zona"
              select
              fullWidth
              value={filter.base_zone_id}
              onChange={(e) => setFilter({ ...filter, base_zone_id: e.target.value })}
            >
              <MenuItem value="">Todas</MenuItem>
              {zones.map((zone) => (
                <MenuItem key={zone.id} value={zone.id}>
                  {zone.description}
                </MenuItem>
              ))}
            </TextField>
          </GridItem>
          <GridItem xs={12} sm={6}>
            <TextField
              label="Estado"
              select
              fullWidth
              value={filter.type}
              onChange={(e) => setFilter({ ...filter, type: e.target.value })}
            >
              <MenuItem value="no_aplicados">No aplicados</MenuItem>
            </TextField>
          </GridItem>
          <GridItem xs={12}>
            <RegularButton
              type="button"
              color="primary"
              size="sm"
              disabled={loading}
              onClick={() => search(filter)}
            >
              Buscar
            </RegularButton>
          </GridItem>

          <GridItem xs={12}>
            <Typography variant="body2" style={{ margin: '10px 0' }}>
              {loading ? (
                <>
                  Buscando documentos <LoadingSpinner />
                </>
              ) : (
                `${porSocio.length} socio(s), ${documents.length} documento(s) a procesar`
              )}
            </Typography>
          </GridItem>

          <GridItem xs={12}>
            {porSocio.map((socio) => (
              <Accordion key={socio.CardCode}>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography variant="body2">
                    {socio.CardCode} — {socio.CardName} ({socio.documents.length} documento(s))
                  </Typography>
                </AccordionSummary>
                <AccordionDetails style={{ display: 'block' }}>
                  {socio.detalle.length === 0 ? (
                    <Typography variant="body2">Sin documentos con NC en este rango.</Typography>
                  ) : (
                    <Table
                      tableHeaderColor="warning"
                      tableHead={['T.Doc', 'Serie', 'Número', 'Num. SAP', 'Fecha', 'NC', 'Saldo']}
                      tableData={socio.detalle.map((doc) => [
                        doc.U_SYP_MDTD,
                        doc.U_SYP_MDSD,
                        doc.U_SYP_MDCD,
                        doc.DocNum,
                        moment(doc.TaxDate).format('DD-MM-YYYY'),
                        doc.CreditNotesAgg.split(',').length,
                        (doc.DocTotal - doc.PaidToDate).toFixed(2),
                      ])}
                    />
                  )}
                </AccordionDetails>
              </Accordion>
            ))}
          </GridItem>
        </GridContainer>
      </ModalBase>
    </>
  );
}

export default ProcessNCModal;
